import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import duration from "dayjs/plugin/duration"
import inflection from "inflection"
import { lookupByEventType } from "@/stores/timeline/eventTypes"

// Lightweight JWT payload decode (replaces jsonwebtoken to reduce bundle size; used only for expiry check)
const decodeJwtPayload = (token) => {
  try {
    const parts = token.split(".")
    if (parts.length !== 3) return null
    let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/")
    const pad = base64.length % 4
    if (pad) base64 += "====".slice(0, 4 - pad)
    return JSON.parse(atob(base64))
  } catch (e) {
    return null
  }
}

const localizedFormat = require("dayjs/plugin/localizedFormat")
dayjs.extend(advancedFormat)
dayjs.extend(localizedFormat)
dayjs.extend(duration)

const isNull = (value) => {
  return value === undefined || value === null
}

const isEmpty = (value) => {
  return isNull(value) || value === ""
}

const isEmptyObj = (obj) => {
  return Object.keys(obj).length === 0
}

const isPresent = (value) => {
  return !isEmpty(value)
}

const capitalize = (value) => {
  if (typeof value === "string") {
    return value.charAt(0).toUpperCase() + value.slice(1)
  } else {
    return value
  }
}

const durationToWords = (durationInSeconds) => {
  if (durationInSeconds) {
    let days = dayjs.duration(durationInSeconds, "seconds").days()
    let hours = dayjs.duration(durationInSeconds, "seconds").hours()
    const minutes = dayjs.duration(durationInSeconds, "seconds").minutes()
    if (days > 0) {
      if (minutes > 30 && hours < 23) hours += 1
      if (minutes > 30 && hours === 23) {
        days += 1
        hours = 0
      }
      return `${days}d ${hours}h`
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`
    } else if (minutes > 0) {
      return `${minutes}m`
    } else {
      return "-"
    }
  } else {
    return "-"
  }
}

const durationToVerboseWords = (durationInSeconds) => {
  if (durationInSeconds) {
    let days = dayjs.duration(durationInSeconds, "seconds").days()
    let hours = dayjs.duration(durationInSeconds, "seconds").hours()
    const minutes = dayjs.duration(durationInSeconds, "seconds").minutes()
    if (days > 0) {
      if (minutes > 30 && hours < 23) hours += 1
      if (minutes > 30 && hours === 23) {
        days += 1
        hours = 0
      }
      return `${days} days ${hours} hours`
    } else if (hours > 0) {
      return `${hours} hours ${minutes} minutes`
    } else if (minutes > 0) {
      return `${minutes} minutes`
    } else {
      return "-"
    }
  } else {
    return "-"
  }
}

const titleize = (value) => (value ? inflection.titleize(value) : "")

const pluralize = (value) => {
  return inflection.pluralize(value)
}

const adjustTimelineCaseTypeDisplayName = (rootState, timelineEvents) => {
  if (timelineEvents) {
    for (const day of timelineEvents) {
      for (const event of day.events) {
        const caseType = getCaseTypeById(rootState, event.caseType.id)
        event.caseType.name = caseTypeName(caseType)
      }
    }
  } else {
    return []
  }
}

const caseTypeName = (caseType) => {
  if (caseType) {
    if (caseType.displayName) {
      return caseType.displayName
    } else {
      return caseType.name || ""
    }
  } else {
    return ""
  }
}

const findBy = (array, params) => {
  return array.filter((obj) => {
    let found = true
    for (const k of Object.keys(params)) {
      const value = params[k]
      if (obj[k] !== value) {
        found = false
      }
    }
    return found
  })[0]
}

const removeUnderscores = (value) => {
  if (value === null || value === undefined) return value
  return value.replace(/_/g, " ")
}

const capitalizeRemoveUnderscores = (value) => {
  if (value === null) return value
  return removeUnderscores(capitalize(value))
}

const replaceSpaces = (value, symbol) => {
  // Not used
  return value.replace(/ /g, symbol)
}

const formatSnakeCase = (value) => {
  return value.replace(/ /g, "_").toLowerCase()
}
const isDate = (date) => {
  return !isNaN(Date.parse(date))
}

const generateNumberArray = (min, max) => {
  const numbers = []
  for (let i = min; i < max + 1; i++) {
    numbers.push(i)
  }
  return numbers
}

// Format date depending on provided dayjs format
const formatDate = (date, format = "DD/MM/YYYY") => {
  const result = dayjs(date).format(format)
  if (result.includes("NaN")) {
    return date
  } else {
    return result
  }
}

const formatDateTime = (date, format = "DD/MM/YYYY h:mma") => {
  const result = dayjs(date).format(format)
  if (result.includes("NaN")) {
    return date
  } else {
    return result
  }
}

const formatTime = (date, format = "HH:mm") => {
  const result = dayjs(date).format(format)
  if (result.includes("NaN")) {
    return date
  } else {
    return result
  }
}

const ONEDAY = 86400
const ONEWEEK = 7 * 86400
const SHORTMONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

const sameday = (date1, date2) => date1.getDate() === date2.getDate() && date1.getMonth() === date2.getMonth() && date1.getYear() === date2.getYear()

const formatTimeAsGmail = (date, format) => {
  const now = new Date()
  const duration = (now.getTime() - date.getTime()) / 1000

  if (sameday(date, now)) {
    const afternoon = date.getHours() > 12
    const ampm = afternoon ? "pm" : "am"
    const hours = afternoon ? date.getHours() - 12 : date.getHours()
    return `${hours}:${date.getMinutes().toString().padStart(2, "0")}${ampm}`
    // 12-hour clock
  } else if (!sameday(date, now) && duration <= ONEWEEK) {
    // short date e.g. Jan 11
    const month = SHORTMONTHS[date.getMonth()]
    return `${month} ${date.getDate()}`
  } else {
    // long date eg. 28/12/20
    return formatDate(date, format)
  }
}

// Format timeline events - used across multiple components
const formatEventDateSimple = (date) => {
  const dateObj = dayjs(date)
  const dateDay = dateObj.date()
  const dateMonth = dateObj.month()
  const dateYear = dateObj.year()

  const currentDay = dayjs().date()
  const currentMonth = dayjs().month()
  const currentYear = dayjs().year()
  // Format if today or yesterday
  if (dateYear === currentYear && dateMonth === currentMonth) {
    if (dateDay === currentDay) return "Today - " + formatDate(date, "Do MMMM YYYY")
    if (dateDay === currentDay - 1) return "Yesterday - " + formatDate(date, "Do MMMM YYYY")
  }
  // Return regular date
  return formatDate(date, "Do MMMM YYYY")
}

const formatDecimalPlaces = (value, places) => {
  return value.toFixed(places)
}

const formatCurrency = (value, symbol = '', precision = 2) => {
  let localValue = 0
  const output = []

  if (symbol) output.push(symbol)

  if (value !== null && !isNaN(parseFloat(value))) {
    if (value.toString() === `-${parseInt("0").toFixed(precision)}`) {
      localValue = 0
    } else {
      localValue = parseFloat(value)
    }

    output.push(localValue.toLocaleString(undefined, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    }))
  } else {
    output.push((0).toLocaleString(undefined, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision
    }))
  }

  return output.join("")
}

const formatNumber = (value) => {
  let result
  
  // Check if the value is a decimal number
  if (value.toString().includes(".")) {
    result = parseFloat(parseFloat(value).toFixed(4))
  } else {
    result = parseInt(value)
  }
  
  // If result is not a number, return the original value
  if (isNaN(result)) {
    return value
  } else {
    // Format with comma separators:
    if (value.toString().includes(".")) {
      // For decimals, ensure four decimal places are shown
      return result.toLocaleString(undefined, {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4
      })
    } else {
      // For integers, default locale formatting with commas
      return result.toLocaleString()
    }
  }
}

const formatNumberSign = (value) => {
  return value > 0 ? "green" : "red"
}

const formatYNboolean = (value) => {
  return value ? "YES" : "NO"
}

const formatAddress = (value) => {
  let address = ""
  if (value.name) {
    address = value.name
  }
  if (value.postal_code) {
    address += ", " + value.postal_code
  }
  if (value.country) {
    address += ", " + value.country
  }
  return address
}

const formatDocument = (value) => {
  if (value === undefined) {
    return "None"
  }
  return value.filename
}

const formatEntity = (value) => {
  if (value !== undefined || value.length < 1) {
    return value.length
  } else {
    return "none"
  }
}

const formatRelation = (value) => {
  let relation = ""
  if (value === null) {
    return "None"
  }
  if (typeof value === "object") {
    // If string, already formatted.
    const data = Object.values(value)
    relation = data[1] + " " + data[2] // TODO - Test all relations make sense with only 2 fields..
    // Object.keys(value).forEach(function(line) {
    //   if (value[line] !== null) {
    //     if (line.charAt(0) !== "_") {
    //       if (relation === "") { relation += capitalize(value[line]) } else { relation += ", " + capitalize(value[line]) } // If empty, no comma..
    //     }
    //   }
    // })
    return relation
  } else {
    return value
  }
}

// Get array index that matches specific property and value
const getArrIndexFromPropMatch = (arr, propToMatch, valToMatch) => {
  // Returns index depending on property and value match
  let indexToReturn = null
  for (let x = 0; x < arr.length; x++) {
    // filterParams[timelineFilterIcons[x].urlParam] = timelineFilterIcons[x].enabled
    if (arr[x][propToMatch] === valToMatch) {
      indexToReturn = x
      break
    }
  }
  return indexToReturn
}

// Return `${case_type}_ref` value depending on case_type
const getCaseRefField = (caseData, caseType) => {
  const fieldName = caseType.replace(/ /g, "_").toLowerCase() + "_ref"
  return caseData[fieldName]
}

const removingTrailingSlash = (val) => {
  if (val.endsWith("/")) return val.substring(0, val.length - 1)
  return val
}

const hasPermission = (permissions, permissionCode, caseTypeCode) => {
  if (permissions && permissions.length > 0) {
    const result = permissions.includes(`${permissionCode}-${caseTypeCode}`)
    return result
  }
  return false
}

const getCaseTypeById = (state, id) => {
  if (state && state.caseTypes && state.caseTypes.all) {
    return state.caseTypes.all.find((currentType) => parseInt(currentType.id, 10) === parseInt(id, 10))
  }
}

const getCaseTypeByCode = (state, code) => {
  if (state && state.caseTypes && state.caseTypes.all) {
    return state.caseTypes.all.find((currentType) => currentType.code === code)
  }
}

const getTimelineTypeByRoute = (route) => {
  let timelineType = ""
  switch (route) {
    case "dashboard":
      timelineType = "user"
      break
    case "case":
      timelineType = "currentCase" // Doesn't exist yet but will likely need updated once added.
      break
    default:
      timelineType = "user"
  }
  return timelineType
}

const calculateTimeDifferenceFromNow = (dueDate) => {
  let diffSeconds = Math.abs(new Date(dueDate) - new Date()) / 1000
  const days = Math.floor(diffSeconds / 86400)
  diffSeconds -= days * 86400
  const hours = Math.floor(diffSeconds / 3600) % 24
  diffSeconds -= hours * 3600
  const minutes = Math.floor(diffSeconds / 60) % 60
  diffSeconds -= minutes * 60
  let dueTime = ""
  if (days > 0) {
    if (days === 1) {
      dueTime += days + " Day "
    } else {
      dueTime += days + " Days "
    }
  }
  if (hours > 0) {
    if (hours === 1) {
      dueTime += hours + " Hour "
    } else {
      dueTime += hours + " Hours "
    }
  }
  if (minutes > 0) {
    if (minutes === 1) {
      dueTime += minutes + " Minute"
    } else {
      dueTime += minutes + " Minutes"
    }
  }
  if (dueTime === "") {
    return Math.floor(diffSeconds) + " Seconds"
  }
  return dueTime
}

const calculateTimeDifference = (start, end) => {
  let diffSeconds = Math.abs(new Date(start) - new Date(end)) / 1000
  const days = Math.floor(diffSeconds / 86400)
  diffSeconds -= days * 86400
  const hours = Math.floor(diffSeconds / 3600) % 24
  diffSeconds -= hours * 3600
  const minutes = Math.floor(diffSeconds / 60) % 60
  diffSeconds -= minutes * 60
  let dueTime = ""
  if (days > 0) {
    if (days === 1) {
      dueTime += days + " Day "
    } else {
      dueTime += days + " Days "
    }
  }
  if (hours > 0) {
    if (hours === 1) {
      dueTime += hours + " Hour "
    } else {
      dueTime += hours + " Hours "
    }
  }
  if (minutes > 0) {
    if (minutes === 1) {
      dueTime += minutes + " Minute "
    } else {
      dueTime += minutes + " Minutes "
    }
  }
  if (diffSeconds > 0) {
    if (diffSeconds === 1) {
      dueTime += diffSeconds + " Second"
    } else {
      dueTime += diffSeconds + " Seconds"
    }
  }
  if (diffSeconds === 0) {
    dueTime = "less than a second"
  }
  return dueTime
}

const calculateDurationFromSeconds = (diffSeconds) => {
  const days = Math.floor(diffSeconds / 86400)
  diffSeconds -= days * 86400
  const hours = Math.floor(diffSeconds / 3600) % 24
  diffSeconds -= hours * 3600
  const minutes = Math.floor(diffSeconds / 60) % 60
  diffSeconds -= minutes * 60
  let dueTime = ""
  if (days > 0) {
    if (days === 1) {
      dueTime += days + "D "
    } else {
      dueTime += days + "D "
    }
  }
  if (hours > 0) {
    if (hours === 1) {
      dueTime += hours + "H "
    } else {
      dueTime += hours + "H "
    }
  }
  if (minutes > 0) {
    if (minutes === 1) {
      dueTime += minutes + "M "
    } else {
      dueTime += minutes + "M "
    }
  }
  if (dueTime === "") {
    return Math.floor(diffSeconds) + "S"
  }
  return dueTime
}

// Build liml params for event detail
const getLinkParams = (activityValues, eventDateObj) => {
  // Dynamically build up params based on activityValues
  const activityValue = lookupByEventType(activityValues, eventDateObj.activity)
  const paramObj = {}

  for (let x = 0; x < activityValue.params.length; x++) {
    // Located in root eventObj or inside .params property
    const locatedInParams = activityValue.params[x].locatedInParams
    paramObj[activityValue.params[x].pathParam] = locatedInParams ? eventDateObj.params[activityValue.params[x].pathParamValue] : eventDateObj[activityValue.params[x].pathParamValue]
  }
  return paramObj
}

const prepareBucketSchema = (caseType, bucket) => {
  let displayFields = []
  if (bucket && bucket.displayFields) {
    displayFields = bucket.displayFields.map((df) => df.fieldName)
  }
  const displayFieldsPresent = displayFields.length > 0

  const schemaFields = displayFieldsPresent ? displayFields : ["title", "created_at", "updated_at", "last_exported_at", "last_exported_via_bucket_id", "current_state", "sentiment_score", "sentiment_magnitude", "no_unread_conversations", "unresolved_task_failures"]
  const unfilteredCommonFields = [
    { name: "title", type: "string" },
    { name: "created_at", type: "date" },
    { name: "updated_at", type: "date" },
    { name: "last_exported_at", type: "date" },
    { name: "last_exported_via_bucket_id", type: "number" },
    { name: "current_state", type: "string" },
    { name: "sentiment_score", type: "number" },
    { name: "sentiment_magnitude", type: "number" },
    { name: "no_unread_conversations", type: "number" },
    { name: "unresolved_task_failures", type: "number" },
  ]

  let slc
  if (bucket) {
    slc = bucket.serviceLevel
  }
  if (slc && slc.enabled) {
    unfilteredCommonFields.splice(1, 0, { name: "run_time", type: "string" }) // If cases have active SLA, add run time field immediately after Title.
    schemaFields.splice(1, 0, "run_time")
  }
  const commonFields = displayFieldsPresent ? unfilteredCommonFields.filter((sf) => displayFields.includes(sf.name)) : unfilteredCommonFields
  const schema = []
  const linkedData = []
  const documents = []
  const rightAlignData = []
  const fieldsets = [{ name: "common", fields: commonFields }].concat(caseType.currentSchema.fieldsets)

  const pickedFields = []
  if (displayFieldsPresent) {
    schemaFields.forEach((schemaField) => {
      fieldsets.forEach(function (fieldset) {
        fieldset.fields.forEach(function (field) {
          if (schemaField === field.name && !pickedFields.includes(field.name)) {
            schema.push(field) // For cases
            pickedFields.push(field.name)
            if (field.type === "relation") {
              linkedData.push(field.name)
            }
            if (field.type === "document") {
              documents.push(field.name)
            }
            if (field.type === "number") {
              rightAlignData.push(field.name)
            }
          }
        })
      })
    })
  } else {
    fieldsets.forEach(function (fieldset) {
      fieldset.fields.forEach(function (field) {
        schema.push(field) // For cases
        pickedFields.push(field.name)
        if (field.type === "relation") {
          linkedData.push(field.name)
        }
        if (field.type === "document") {
          documents.push(field.name)
        }
        if (field.type === "number") {
          rightAlignData.push(field.name)
        }
      })
    })
  }
  return { schema: schema, schemaFields, linkedData, documents, rightAlignData, displayFieldsPresent }
}

const RELATION_LOOKUP_PAGE_SIZE = 250

const SEARCH_INTERNAL_COMMON_FIELDS = {
  last_exported_at: true,
  last_exported_via_bucket_id: true,
  sentiment_score: true,
  sentiment_magnitude: true,
  no_unread_conversations: true,
  unresolved_task_failures: true,
  owner_name: true
}

const isTruthyFlag = (value) => {
  return value === true || value === "true" || value === "yes"
}

const isHiddenFieldset = (fieldset) => {
  if (!fieldset) { return true }
  if (isTruthyFlag(fieldset.hidden)) { return true }
  if (fieldset.display === "hidden" || fieldset.display === "none") { return true }
  return false
}

const isExcludedSearchField = (field) => {
  if (!field || !field.name) { return true }
  if (SEARCH_INTERNAL_COMMON_FIELDS[field.name]) { return true }
  if (isTruthyFlag(field.hidden)) { return true }
  if (isTruthyFlag(field.internal)) { return true }
  if (field.type === "password") { return true }
  return false
}

const prepareSearchSchema = (caseType) => {
  const commonFields = [
    { name: "title", type: "string" },
    { name: "current_state", type: "string" },
    { name: "updated_at", type: "date" },
    { name: "created_at", type: "date" }
  ]
  const schema = []
  const linkedData = []
  const documents = []
  const rightAlignData = []
  const pickedFields = []
  const schemaFields = []

  const pushField = (field, asLink) => {
    if (isExcludedSearchField(field)) { return }
    if (pickedFields.indexOf(field.name) !== -1) { return }
    schema.push(field)
    pickedFields.push(field.name)
    schemaFields.push(field.name)
    if (asLink || field.name === "title" || field.type === "relation" || field.type === "document") {
      linkedData.push(field.name)
    }
    if (field.type === "document") {
      documents.push(field.name)
    }
    if (field.type === "number") {
      rightAlignData.push(field.name)
    }
  }

  commonFields.forEach(function (field) {
    pushField(field, field.name === "title")
  })

  const schemaFieldsets = (caseType && caseType.currentSchema && caseType.currentSchema.fieldsets)
    ? caseType.currentSchema.fieldsets
    : []
  schemaFieldsets.forEach(function (fieldset) {
    if (isHiddenFieldset(fieldset)) { return }
    const fields = fieldset.fields || []
    fields.forEach(function (field) {
      pushField(field, false)
    })
  })

  return {
    schema: schema,
    schemaFields: schemaFields,
    linkedData: linkedData,
    documents: documents,
    rightAlignData: rightAlignData,
    displayFieldsPresent: true
  }
}

const getBucketHeaderLink = (schemaData) => {
  if (schemaData && schemaData.schema) {
    const headers = schemaData.schema
    return headers[0] && headers[0].name ? headers[0].name : null
  }
}

const formatBucketValue = (currentCase, currentValue, fieldName, fieldType, bucketHeaderLink) => {
  if (bucketHeaderLink === fieldName) {
    const cellValue = fieldType === "relation" || fieldType === "document" ? currentValue.value : currentValue

    const formattedValue = {
      value: cellValue,
      route: { name: "Case", params: { case_id: currentCase.id } },
    }
    if (fieldType === "document") formattedValue.documentRoute = currentValue.route
    return formattedValue
  }
  return currentValue
}

const formatBucketHeaders = ({ schemaFields, schema, linkedData, documents, rightAlignData, displayFieldsPresent }, bucketHeaderLink = "title") => {
  const headers = displayFieldsPresent ? [] : []
  const headerNames = []
  if (schema) {
    schema.forEach(function (header) {
      let link = false
      let right = false
      let doc = false
      let sla = false
      let embed = false
      let canSort = true
      if ((linkedData && linkedData.includes(header.name)) || header.name === bucketHeaderLink) {
        link = true
      }
      if ((linkedData && linkedData.includes(header.name)) || (documents && documents.includes(header.name) && header.name !== "title")) {
        canSort = false
      }
      if (header.type === "location" || header.type === "address" || header.type === "entity" || header.type === "relation") {
        canSort = false
      }
      if (documents && documents.includes(header.name)) {
        doc = true
      }
      if (rightAlignData && rightAlignData.includes(header.name)) {
        right = true
      }
      if (header.name === "run_time") {
        canSort = false
        sla = true
      }
      if (doc || link || sla) {
        embed = true
      }
      if (schemaFields.length === 0 || schemaFields.includes(header.name) || !displayFieldsPresent) {
        // If no schema - format everything. If schema found, format specifics. Ignore others.
        if (headerNames.indexOf(header.name) === -1) {
          const headerName = header.display_name ? header.display_name : capitalizeRemoveUnderscores(header.name)
          headers.push({ text: headerName, value: header.name, sortable: canSort, hasLink: link, hasDocument: doc, rightAlign: right, serviceLevel: sla, embedded: embed })
          headerNames.push(header.name)
        }
      }
    })
  }
  return headers
}

const PASSWORD_FIELD_MASK = "********"

const getBucketFieldFormatting = (caseValue, field, preferences) => {
  switch (field.type) {
    case "boolean":
      return caseValue === true ? "Yes" : "No"
    case "password":
      if (caseValue === null || caseValue === undefined || caseValue === "") {
        return ""
      }
      return PASSWORD_FIELD_MASK
    case "email_address":
    case "string":
    case "text":
      return caseValue
    case "number":
      if (caseValue === null) {
        break
      }
      if (field.symbol !== null && field.symbol !== undefined) {
        if (field.display === "currency") {
          const float = parseFloat(caseValue)
          return !isNaN(float) ? formatCurrency(parseFloat(caseValue), field.symbol) : "-"
        }
      } else {
        return caseValue
      }
      break
    case "date":
      if (caseValue === null) {
        break
      }
      if (caseValue.includes('"')) {
        // If Date arrives in quotes..
        caseValue = caseValue.replace(/"/g, "")
      }
      if (field.format !== null && field.format !== undefined) {
        return formatDate(caseValue, field.format) // Use specified format
      } else {
        // or use user preference.
        if (field.display === "date_only") {
          return formatDate(caseValue, preferences.dateFormat)
        }
        return formatDate(caseValue, preferences.dateTimeFormat)
      }
    case "address":
    case "location":
      if (caseValue === null) {
        break
      }
      return formatAddress(caseValue)
    case "relation": {
      if (caseValue === null) {
        return { value: "-", route: "" }
      } else {
        if (caseValue && caseValue.id) {
          const relatedCase = caseValue.summary
          const relationObj = {}
          relationObj.route = { name: "Case", params: { case_id: caseValue.id } }

          // Recursive call for summary field formatting
          if (relatedCase) {
            let content = relatedCase.summaryVal1
            if (content) content = getBucketFieldFormatting(relatedCase.summaryVal1, JSON.parse(relatedCase.summaryVal1Defn), preferences)

            if (relatedCase.summaryVal2) {
              content += " " + getBucketFieldFormatting(relatedCase.summaryVal2, JSON.parse(relatedCase.summaryVal2Defn), preferences)
            }
            if (relatedCase.summaryVal3) {
              content += " " + getBucketFieldFormatting(relatedCase.summaryVal3, JSON.parse(relatedCase.summaryVal3Defn), preferences)
            }
            if (relatedCase.summaryVal4) {
              content += " " + getBucketFieldFormatting(relatedCase.summaryVal4, JSON.parse(relatedCase.summaryVal4Defn), preferences)
            }
            if (relatedCase.summaryVal5) {
              content += " " + getBucketFieldFormatting(relatedCase.summaryVal5, JSON.parse(relatedCase.summaryVal5Defn), preferences)
            }
            relationObj.value = content
            return relationObj
          }
          // Fallback when API returns relation without summary (e.g. related case not in batch lookup)
          if (caseValue.title) {
            relationObj.value = caseValue.title
            return relationObj
          }
          if (caseValue.properties && caseValue.properties.title) {
            relationObj.value = caseValue.properties.title
            return relationObj
          }
          return { value: "-", route: "" }
        }
      }
      break
    }
    case "entity":
      if (caseValue === null) {
        break
      }
      return formatEntity(caseValue)
    case "document": {
      const docObj = {}
      if (!caseValue || !caseValue.filename) {
        docObj.value = "-"
        docObj.route = ""
      } else {
        docObj.value = formatDocument(caseValue)
        docObj.route = caseValue.url
      }
      return docObj
    }
    case "case_type_states":
      return caseValue
    default:
      console.error("UNHANDLED TYPE! " + field.type)
  }
}

const formatBucketData = (schemaData, cases, preferences, slc, bucketHeaderLink = "title") => {
  if (schemaData.schema === undefined || schemaData.schema === null) {
    console.error("Schema undefined!")
    return cases
  }
  for (let index = 0; index < cases.length; index++) {
    const currentCase = cases[index]
    const currentProperties = currentCase.properties
    if (slc && currentCase.serviceLevels && Array.isArray(currentCase.serviceLevels)) {
      currentCase.serviceLevels.forEach((sla) => {
        if (sla && sla.serviceLevelCriteria && sla.serviceLevelCriteria.id === slc) {
          const runTime = calculateDurationFromSeconds(sla.runningTime)
          currentProperties.run_time = { value: runTime, sla: sla }
        }
      })
    }
    currentProperties.title = currentCase.title
    currentProperties.created_at = currentCase.createdAt
    currentProperties.updated_at = currentCase.updatedAt
    currentProperties.last_exported_at = currentCase.lastExportedAt
    currentProperties.last_exported_via_bucket_id = currentCase.lastExportedViaBucketId
    currentProperties.no_unread_conversations = currentCase.noUnreadConversations
    currentProperties.unresolved_task_failures = currentCase.unresolvedTaskFailures
    currentCase.properties.sentiment_score = currentCase.sentimentScore
    currentCase.properties.sentiment_magnitude = currentCase.sentimentMagnitude
    currentProperties.current_state = currentCase.currentState
    for (let index = 0; index < schemaData.schema.length; index++) {
      const field = schemaData.schema[index]
      if (currentCase.properties[field.name] === undefined) {
        if (field.default_value === undefined) {
          currentCase.properties[field.name] = null
        } else {
          currentCase.properties[field.name] = field.default_value
        }
      }
      const fieldValue = getBucketFieldFormatting(currentCase.properties[field.name], field, preferences)
      if (fieldValue !== undefined) currentCase[field.name] = fieldValue
      currentCase[field.name] = formatBucketValue(currentCase, currentCase[field.name], field.name, field.type, bucketHeaderLink)
    }
    cases[index] = currentCase
  }
  return cases
}

const adjustToLocalTimeZone = (d) => {
  if (!d) return null
  const initialDate = new Date(d)

  const offset = initialDate.getTimezoneOffset() * 60 * 1000
  if (offset === 0) return initialDate.toISOString()

  const initialDateInMilliseconds = initialDate.getTime()
  const newDateInMilliseconds = offset > 0 ? initialDateInMilliseconds - offset : initialDateInMilliseconds + offset
  return new Date(newDateInMilliseconds).toISOString()
}

const adjustToLocalMidnight = (d) => {
  if (d) {
    return dayjs(d).startOf("day").toISOString()
  }
  return d
}

const generateUUID = () => {
  // uuid v4
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const uploadFile = async (file, path) => {
  console.log("Arrived at UploadFile with path: ", path)
  const url = window.location.href.startsWith("http://localhost") ? `http://localhost:6543${path}` : path
  const auth = "Bearer " + window.localStorage.session
  const formData = new FormData()
  formData.append("file", file)
  await fetch(url, {
    method: "POST",
    body: formData,
    headers: { Authorization: auth },
  })
}

const orderItemsByPosition = (items) => {
  if (items && Array.isArray(items)) {
    return items.sort(function (a, b) {
      if (a.position < b.position) {
        return -1
      }
      if (a.position > b.position) {
        return 1
      }
      return 0
    })
  } else {
    return items
  }
}

const updateItemPositions = (items) => {
  if (items && Array.isArray(items)) {
    items.forEach((item, index) => {
      item.position = index
    })
  }
}

const isValidFolderOrFileName = (name) => {
  if (!name) return false
  const illegalCharacters = ["/", "\\"]
  const containsIllegalCharacters = illegalCharacters.some((char) => name.includes(char))
  const precedingPeriod = name.startsWith(".")
  return !containsIllegalCharacters && !precedingPeriod
}

const formatFolder = (folder) => {
  return {
    id: folder.id,
    parentFolderId: folder.parentFolderId,
    name: folder.folderName,
    path: folder.folderPath,
    templateDefined: true, // no longer used?
    modified: "Folder",
    icon: "folder",
    type: "folder",
  }
}

const isJwtValid = (token) => {
  if (token) {
    const decoded = decodeJwtPayload(token)
    if (decoded && decoded.exp) {
      const expiryDate = new Date()
      expiryDate.setTime(parseInt(decoded.exp) * 1000)
      return expiryDate.getTime() > new Date().getTime()
    }
  }
  return false
}

const currentEnvironment = () => {
  const hostParts = window.location.hostname.split(".")
  if (hostParts[0] === "localhost") {
    return "local"
  } else if (hostParts[1] === "staging") {
    return "staging"
  } else {
    return "production"
  }
}

const chatMessageData = (message) => {}

export default {
  isEmpty,
  isEmptyObj,
  isNull,
  isPresent,
  titleize,
  capitalize,
  pluralize,
  findBy,
  removeUnderscores,
  replaceSpaces,
  capitalizeRemoveUnderscores,
  isDate,
  getCaseTypeById,
  getCaseTypeByCode,
  generateNumberArray,
  formatDate,
  formatDateTime,
  formatTime,
  formatEventDateSimple,
  getArrIndexFromPropMatch,
  formatDecimalPlaces,
  formatCurrency,
  formatNumber,
  formatNumberSign,
  formatSnakeCase,
  formatYNboolean,
  formatAddress,
  formatDocument,
  formatEntity,
  formatRelation,
  getCaseRefField,
  removingTrailingSlash,
  hasPermission,
  getTimelineTypeByRoute,
  calculateTimeDifferenceFromNow,
  calculateTimeDifference,
  calculateDurationFromSeconds,
  getLinkParams,
  caseTypeName,
  adjustTimelineCaseTypeDisplayName,
  getBucketFieldFormatting,
  formatBucketData,
  formatBucketHeaders,
  getBucketHeaderLink,
  formatBucketValue,
  prepareBucketSchema,
  prepareSearchSchema,
  RELATION_LOOKUP_PAGE_SIZE,
  adjustToLocalTimeZone,
  uploadFile,
  generateUUID,
  formatTimeAsGmail,
  orderItemsByPosition,
  updateItemPositions,
  isValidFolderOrFileName,
  formatFolder,
  durationToWords,
  durationToVerboseWords,
  isJwtValid,
  adjustToLocalMidnight,
  currentEnvironment,
}
